import './perks-post-media.component.scss';

import { h } from 'preact';
import { useMemo } from 'preact/hooks';

import { selectIsAuthenticated } from '../../../../../auth/store/auth.selectors';
import { IconLock } from '../../../../../common/icons/icon-lock';
import { useTranslation } from '../../../../../i18n';
import { PostAudioPlayer } from '../../../../../music/components/post-audio-player/post-audio-player.component';
import { useAppSelector } from '../../../../../store/hooks';
import type { Post } from '../../../../model/post.interface';
import { PerksHelper } from '../../../../perks.helper';
import { hiddenPostImage } from './hidden-image-base-64';
import { PerksPostMediaContent } from './perks-post-media-content.component';

interface Props {
  post: Post;
  showSlider: boolean;
  showPlayer: boolean;
  previewMode?: boolean;
  onJoinPreviewClick?: () => void;
}

export const PerksPostMedia = ({
  post,
  showSlider,
  showPlayer,
  previewMode,
  onJoinPreviewClick,
}: Props) => {
  const { t } = useTranslation();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);

  const hasContents = post.contents?.length > 0;

  // If no contents and not in previewMode mode, don't show anything
  if (!hasContents && !previewMode) {
    return null;
  }

  const showPlayerMemo = useMemo(() => {
    if (previewMode) {
      return false;
    }

    return (
      showPlayer &&
      (PerksHelper.isVideo(post.contents) || PerksHelper.isAudio(post.contents)) &&
      post?.userHasAccess &&
      !post?.hidden &&
      (isAuthenticated || post?.publicPreview)
    );
  }, [previewMode, post, isAuthenticated]);

  if (hasContents && !post.hidden && !previewMode) {
    return (
      <PerksPostMediaContent
        post={post}
        showSlider={showSlider}
        showPlayer={showPlayerMemo}
        showNeedAccessPlayBtn={true}
      />
    );
  }

  if (!hasContents && !previewMode) {
    return null;
  }

  // Locked audio post: keep the player silhouette — blurred art, ghost meta,
  // play button opens the need-access modal.
  if (PerksHelper.isAudio(post.contents) && !previewMode) {
    return (
      <div className="single-post-media post-hidden">
        <PostAudioPlayer
          contentId={post.id}
          postId={post.id}
          locked={true}
          artworkUrl={hiddenPostImage}
        />
      </div>
    );
  }

  return (
    <div
      className={`single-post-media post-hidden ${previewMode ? 'single-join-to-access-image' : ''}`}
    >
      <div className="image-container">
        <img src={hiddenPostImage} alt={t('perks.hiddenPostImageAlt')} />
        <div className="single-image-locked-content">
          {previewMode && (
            <button className="btn button" onClick={onJoinPreviewClick}>
              <IconLock />
              <span>{t('access.joinToUnlock')}</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
